// ---------------------------------------------------------------------------
// SavePersonaModal -- Save Funnel Configuration as Named Persona
// ---------------------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { savePersonaSchema, type SavePersonaFormValues } from './savePersonaSchema';

interface SavePersonaModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (name: string, description: string) => Promise<void>;
  readonly isSaving: boolean;
  readonly layerCount: number;
  readonly finalCount: number;
  readonly finalUnit: string;
}

export const SavePersonaModal: React.FC<SavePersonaModalProps> = ({
  open,
  onClose,
  onSave,
  isSaving,
  layerCount,
  finalCount,
  finalUnit,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof SavePersonaFormValues, string>>>({});

  // Clear form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate with Zod
    const result = savePersonaSchema.safeParse({ name, description });

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof SavePersonaFormValues, string>> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof SavePersonaFormValues;
        if (!fieldErrors[field]) {
          fieldErrors[field] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    await onSave(name, description);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Save className="h-5 w-5 text-indigo-500" />
            Save Persona
          </DialogTitle>
          <DialogDescription className="sr-only">Save the current filter configuration as a persona</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Context Info */}
          <div className="bg-v11-surface-hover border border-v11-border rounded-lg p-3">
            <div className="text-xs text-v11-text-muted">You are saving:</div>
            <div className="text-sm font-semibold text-v11-text-primary mt-1">
              {layerCount} layer{layerCount !== 1 ? 's' : ''} → {finalCount.toLocaleString()} {finalUnit}
            </div>
          </div>

          {/* Name Input */}
          <div className="space-y-2">
            <Label htmlFor="persona-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="persona-name"
              placeholder="e.g., US Male Sports Fans"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className={errors.name ? 'border-red-500' : ''}
              autoFocus
            />
            {errors.name && (
              <p className="text-xs text-red-500">{errors.name}</p>
            )}
            <p className="text-xs text-v11-text-muted">
              {name.length}/100 characters
            </p>
          </div>

          {/* Description Input */}
          <div className="space-y-2">
            <Label htmlFor="persona-description">Description (optional)</Label>
            <Textarea
              id="persona-description"
              placeholder="e.g., Males 18-35 in the US who follow NBA content"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              className={errors.description ? 'border-red-500' : ''}
            />
            {errors.description && (
              <p className="text-xs text-red-500">{errors.description}</p>
            )}
            <p className="text-xs text-v11-text-muted">
              {description.length}/500 characters
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Persona
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
